import React, { useState, useCallback, useEffect } from 'react';
import { Box, Button, Divider, IconButton, Tooltip, Typography } from '@mui/material';
import { GridCellParams, GridColDef, GridSortModel, GridRowSelectionModel } from '@mui/x-data-grid';
import { DataGrid, DataGridModel } from 'src/shared/components/organisms/dataGrid';
import { Clear, PostAdd } from '@mui/icons-material';
import { FixedFooter } from 'src/shared/components/navigations/fixedFooter';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { createAccountingRecords, getPayments } from 'src/shared/services/paymentService';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { getInvoiceGroupById } from 'src/shared/services/invoiceGroupService';
import { InvoiceGroup } from 'src/shared/models/invoiceGroup/invoiceGroup';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { toast } from 'react-toastify';
import { AmountWithCurrency } from 'src/shared/components/atoms/amountWithCurrency';
import { DEFAULT_PAGE_INDEX, DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from 'src/shared/models/enums/gridFilterConfig';
import { PaymentGroupedByDate } from '../models/paymentGroupedByDate';
import { PaymentTransactionSearchFilter } from '../models/paymentTransactionSearchFilter';

type PaymentTransactionsDataGridProps = {
  searchFilter: PaymentTransactionSearchFilter;
  rowClicked: (paymentTransaction: PaymentGroupedByDate) => void;
};

const PaymentTransactionsDataGrid = (props: PaymentTransactionsDataGridProps) => {
  const { searchFilter, rowClicked } = props;
  const { t } = useTranslation(['member', 'event', 'common']);
  const { currentUser, dateStamp } = useGlobals();
  const isMounted = useIsMounted();
  const [rows, setRows] = useState<PaymentGroupedByDate[]>([]);
  const [data, setData] = React.useState<DataGridModel>({
    loading: false, // set to true later
    totalRows: 0, // set to other prop later
    rowsPerPageOptions: PAGE_SIZE_OPTIONS,
    pageSize: DEFAULT_PAGE_SIZE,
    page: DEFAULT_PAGE_INDEX,
  });

  const [selectionModel, setSelectionModel] = React.useState<GridRowSelectionModel>([]);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'transactionDate', sort: 'desc' }]);

  const updateData = (k, v) => setData((prev) => ({ ...prev, [k]: v }));

  const onPageChange = (newPageIndex: number) => {
    updateData('page', newPageIndex);
  };

  const onPageSizeChange = (newPageSize: number) => {
    updateData('page', 0);
    updateData('pageSize', newPageSize);
  };

  const handleRowClick = (params) => {
    const item = rows.filter((x) => x.id === params.row.id)[0];
    rowClicked(item);
  };

  const fetchData = useCallback(async () => {
    updateData('loading', true);
    try {
      const res = await getPayments(data.page, data.pageSize, sortModel[0].field, sortModel[0].sort, searchFilter.searchString, searchFilter.ownerId);
      if (isMounted()) {
        updateData('totalRows', res.data.totalItems);
        setRows(res.data.results);
        ConsoleHelper.log('rows', res.data.results, dateStamp);
      }
      ConsoleHelper.log(res);
    } catch (error) {
      if (isMounted()) ConsoleHelper.log(error);
    } finally {
      if (isMounted()) updateData('loading', false);
    }
  }, [data.page, data.pageSize, isMounted, searchFilter.ownerId, searchFilter.searchString, sortModel, dateStamp]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const [currentInvoiceGroup, setCurrentInvoiceGroup] = useState<InvoiceGroup>();
  useEffect(() => {
    const fetchInvoiceGroup = async () => {
      try {
        const res = await getInvoiceGroupById(searchFilter.ownerId || 0);
        if (isMounted()) {
          setCurrentInvoiceGroup(res.data);
        }
      } catch (error) {
        if (isMounted()) {
          ConsoleHelper.log(error);
        }
      }
    };
    if (searchFilter.ownerId) fetchInvoiceGroup();
  }, [isMounted, searchFilter.ownerId]);

  const columns: GridColDef[] = [
    {
      field: 'transactionDate',
      headerName: t('invoice:payment.gridColumns.date'),
      flex: 1,
      renderCell: (params: GridCellParams) => {
        return <>{dayjs(params.row.transactionDate).format('YYYYMMDD')}</>;
      },
    },
    {
      field: 'transactionSum',
      headerName: t('invoice:payment.gridColumns.sum'),
      headerAlign: 'right',
      align: 'right',
      flex: 0.75,
      renderCell: (params: GridCellParams) => {
        const { transactionSum } = params.row;
        return (transactionSum && <AmountWithCurrency amount={transactionSum} />) || '-';
      },
    },
    {
      field: 'count',
      headerName: t('invoice:payment.gridColumns.count'),
      flex: 0.75,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.count}</>;
      },
    },
    {
      field: 'invoiceGroupName',
      headerName: t('invoice:payment.gridColumns.invoiceGroup'),
      flex: 2,
      renderCell: (params: GridCellParams) => {
        return <>{params.row.invoiceGroupName}</>;
      },
    },
  ];

  const disableCreateAccountingRecords = () => {
    return currentInvoiceGroup?.sharedAccount && !getPermission(currentUser, PermissionEnum.BGPaymentSharedAccountRead);
  };

  const WrapButtonWithTooltip = ({ children }) => {
    if (disableCreateAccountingRecords()) {
      return (
        <Tooltip title={t('invoice:transactionPage.disableCreateAccountingRecords')} arrow>
          <span>{children}</span>
        </Tooltip>
      );
    }
    return children;
  };

  const handleCreateAccountingRecords = async (items, ownerId) => {
    updateData('loading', true);
    try {
      await createAccountingRecords(items, ownerId);
      if (isMounted()) toast.success(<>{t('invoice:transactionPage.createAccountingRecordsSuccess')}</>);
    } catch (error) {
      if (isMounted()) ConsoleHelper.log(error);
    } finally {
      if (isMounted()) updateData('loading', false);
    }
  };

  return (
    <Box
      sx={{
        position: 'relative',
        '& .MuiPaper-root': {
          boxShadow: 'none',
        },
      }}
    >
      {rows && columns && (
        <DataGrid<PaymentGroupedByDate>
          autoHeight
          columns={columns}
          rows={rows}
          dataModel={data}
          checkboxSelection
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          onSelectionModelChange={(newSelectionModel) => {
            setSelectionModel(newSelectionModel);
          }}
          disableSelectionOnClick
          onRowClick={handleRowClick}
          selectionModel={selectionModel}
          sortModel={sortModel}
          onSortModelChange={(newSortModel) => {
            if (newSortModel.length) {
              setSortModel(newSortModel);
            } else {
              setSortModel([{ field: sortModel[0].field, sort: sortModel[0].sort === 'asc' ? 'desc' : 'asc' }]);
            }
          }}
        />
      )}
      {selectionModel && selectionModel.length > 0 && (
        <FixedFooter>
          <Box sx={{ p: 1.5, display: 'flex' }}>
            <IconButton onClick={() => setSelectionModel([])} sx={{ pr: 1 }} size='large'>
              <Clear fontSize='small' />
            </IconButton>
            <Typography variant='h6' sx={{ fontWeight: 400 }}>
              {t('common:statusMessages.footerSelectedCount', { count: selectionModel.length })}
            </Typography>
            <Divider orientation='vertical' flexItem sx={{ mx: 1.5 }} />
            {currentInvoiceGroup && (
              <WrapButtonWithTooltip>
                <Button
                  variant='contained'
                  color='success'
                  disableElevation
                  endIcon={<PostAdd fontSize='small' />}
                  disabled={disableCreateAccountingRecords()}
                  onClick={() => {
                    const selectedIDs = Array.from(new Set(selectionModel));
                    const items = rows.filter((x) => selectedIDs.includes(x.id)).map((x) => x.transactionDate);
                    if (searchFilter.ownerId) {
                      handleCreateAccountingRecords(items, searchFilter.ownerId);
                    }
                  }}
                  sx={{ mr: 1 }}
                >
                  {t('invoice:transactionPage.createAccountingRecordsButton')}
                </Button>
              </WrapButtonWithTooltip>
            )}
          </Box>
        </FixedFooter>
      )}
    </Box>
  );
};

export default PaymentTransactionsDataGrid;
